/**
 * `FeaturesInUseCard` — ADR-0263 S4 staff advisory for an account.
 *
 * GET `/internal/admin/accounts/:id/features-in-use` on mount. Lists,
 * per site, which features the site uses today and which of them its
 * resolved v2 plan does NOT include. Staff reads it before flipping
 * `enforcement_enabled` so they can see who would be affected.
 *
 * This surface never writes. INERT today: with the v2 catalog in
 * draft every site reports `plan_source: 'none'` and `outside_plan`
 * stays empty.
 */

import { useEffect, useState } from 'react';

import type {
  AdminAccountFeaturesInUseResponse,
  AdminAccountFeaturesInUseSiteRow,
  AdminFeatureKey,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { useC } from '../../lib/theme.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface FeaturesInUseCardProps {
  accountId: string;
}

const FEATURE_LABEL: Readonly<Record<AdminFeatureKey, string>> = {
  blocking: 'Blocking',
  custom_attack_paths: 'Your own protected paths',
  slack_held_alerts: 'Slack held-order alerts',
  assistant_blocking: 'Assistant blocking',
  white_label: 'White-label',
  agency_portal: 'Agency portal',
};

export function FeaturesInUseCard({ accountId }: FeaturesInUseCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [data, setData] = useState<AdminAccountFeaturesInUseResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getAccountFeaturesInUse(accountId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof ApiError
          ? `${err.kind}${err.status !== null ? `:${err.status}` : ''}`
          : err instanceof Error
            ? err.message
            : 'load_failed';
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, api]);

  const hasViolations = data !== null
    && (data.sites.some((s) => s.outside_plan.length > 0)
      || data.account.features_outside_plan.length > 0);

  return (
    <Card>
      <div style={{ padding: '16px 20px' }}>
        <div style={{
          fontFamily: monoFont, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
          color: C.muted, marginBottom: 4,
        }}>
          Features in use outside the plan
        </div>
        <div style={{
          fontFamily: serifFont, fontSize: 22, lineHeight: 1.15, color: C.text,
          marginBottom: 10,
        }}>
          Who would be affected before enforcement flips on
        </div>
        {loading && (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Loading...
          </div>
        )}
        {error !== null && !loading && (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Could not load this list ({error}).
          </div>
        )}
        {data !== null && !loading && error === null && (
          <>
            <div style={{
              fontFamily: sansFont, fontSize: 13, color: C.muted, marginBottom: 12,
            }}>
              Enforcement is {data.enforcement_enabled ? 'on' : 'off'}. {hasViolations
                ? 'The rows below use features their resolved plan does not include.'
                : 'No site is using a feature outside its plan today.'}
            </div>
            {data.account.features_outside_plan.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{
                  fontFamily: monoFont, fontSize: 11, letterSpacing: 1,
                  color: C.muted, textTransform: 'uppercase', marginBottom: 4,
                }}>
                  Account-level, kept on by staff toggle
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {data.account.features_outside_plan.map((f) => (
                    <Pill key={f.feature} tone="neutral">
                      {FEATURE_LABEL[f.feature]}
                    </Pill>
                  ))}
                </div>
              </div>
            )}
            <SiteRows sites={data.sites} />
          </>
        )}
      </div>
    </Card>
  );
}

function SiteRows({
  sites,
}: {
  sites: readonly AdminAccountFeaturesInUseSiteRow[];
}): JSX.Element {
  const C = useC();
  if (sites.length === 0) {
    return (
      <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
        No live sites.
      </div>
    );
  }
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '6px 16px',
      alignItems: 'baseline',
    }}>
      <div style={{
        fontFamily: monoFont, fontSize: 11, letterSpacing: 1, color: C.muted,
        textTransform: 'uppercase',
      }}>Site</div>
      <div style={{
        fontFamily: monoFont, fontSize: 11, letterSpacing: 1, color: C.muted,
        textTransform: 'uppercase',
      }}>In use</div>
      <div style={{
        fontFamily: monoFont, fontSize: 11, letterSpacing: 1, color: C.muted,
        textTransform: 'uppercase',
      }}>Outside plan</div>
      {sites.map((site) => (
        <SiteRow key={site.site_id} site={site} />
      ))}
    </div>
  );
}

function SiteRow({ site }: { site: AdminAccountFeaturesInUseSiteRow }): JSX.Element {
  const C = useC();
  return (
    <>
      <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}>
        <div>{site.domain ?? site.name ?? site.site_id}</div>
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
          {site.plan_slug ?? 'no plan'}
          {site.plan_source === 'none' ? ' (grandfathered)' : ''}
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 220 }}>
        {site.in_use.length === 0 ? (
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>-</span>
        ) : site.in_use.map((f) => (
          <Pill key={f} tone="neutral">{FEATURE_LABEL[f]}</Pill>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 220 }}>
        {site.outside_plan.length === 0 ? (
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>-</span>
        ) : site.outside_plan.map((f) => (
          <Pill key={f} tone="amber">{FEATURE_LABEL[f]}</Pill>
        ))}
      </div>
    </>
  );
}
